import {Injectable} from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class CryptService {

  private databasePromise: Promise<IDBDatabase> | null = null;

  constructor() {
    this.openDatabase();
  }

  public openDatabase(): Promise<IDBDatabase> {
    if (this.databasePromise) {
      return this.databasePromise;
    }

    return this.databasePromise = new Promise((resolve, reject) => {
      const request = indexedDB.open("cryptoStore", 2);

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains("keys")) {
          db.createObjectStore("keys", {keyPath: "name"});
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async generateKeyPair(): Promise<{ privateKey: CryptoKey; publicKey: ArrayBuffer }> {
    const db = await this.openDatabase();
    const keyPair = await window.crypto.subtle.generateKey(
      {
        name: "RSA-OAEP",
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: "SHA-256",
      },
      true,
      ["encrypt", "decrypt"]
    );

    const exportedPublicKey = await window.crypto.subtle.exportKey("spki", keyPair.publicKey);
    await this.saveKeyToIndexedDB("privateKey", keyPair.privateKey);
    await this.saveKeyToIndexedDB("publicKey", exportedPublicKey);
    return {privateKey: keyPair.privateKey, publicKey: exportedPublicKey};
  }

  /**
   * Crittografia ibrida con AES-GCM + RSA-OAEP
   * Restituisce una stringa base64 JSON contenente: encryptedData, encryptedKey e iv
   */
  async encryptData(data: string, publicKey: ArrayBuffer): Promise<string> {
    // 1. Genera chiave AES
    const aesKey = await crypto.subtle.generateKey(
      {name: "AES-GCM", length: 256},
      true,
      ["encrypt", "decrypt"]
    );

    // 2. Cifra i dati con AES
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encodedData = new TextEncoder().encode(data);
    const encryptedData = await crypto.subtle.encrypt(
      {name: "AES-GCM", iv},
      aesKey,
      encodedData
    );

    // 3. Cifra la chiave AES con RSA
    const rsaKey = await crypto.subtle.importKey(
      "spki",
      publicKey,
      {name: "RSA-OAEP", hash: "SHA-256"},
      false,
      ["encrypt"]
    );

    const rawAesKey = await crypto.subtle.exportKey("raw", aesKey);
    const encryptedAesKey = await crypto.subtle.encrypt(
      {name: "RSA-OAEP"},
      rsaKey,
      rawAesKey
    );

    // 4. Restituisci JSON base64-encoded
    return btoa(JSON.stringify({
      encryptedData: this.arrayBufferToBase64(encryptedData),
      encryptedKey: this.arrayBufferToBase64(encryptedAesKey),
      iv: this.arrayBufferToBase64(iv.buffer),
    }));
  }

  async decryptData(encryptedPayloadBase64: string): Promise<string | null> {
    try {
      const privateKey = await this.getKeyFromIndexedDB("privateKey");
      if (!privateKey) return null;

      const {encryptedData, encryptedKey, iv} = JSON.parse(atob(encryptedPayloadBase64));

      const encryptedKeyBuffer = this.base64ToArrayBuffer(encryptedKey);
      const rawAesKey = await crypto.subtle.decrypt(
        {name: "RSA-OAEP"},
        privateKey,
        encryptedKeyBuffer
      );

      const aesKey = await crypto.subtle.importKey(
        "raw",
        rawAesKey,
        {name: "AES-GCM"},
        false,
        ["decrypt"]
      );

      const decrypted = await crypto.subtle.decrypt(
        {name: "AES-GCM", iv: this.base64ToArrayBuffer(iv)},
        aesKey,
        this.base64ToArrayBuffer(encryptedData)
      );

      return new TextDecoder().decode(decrypted);
    } catch (error) {
      return null;
    }
  }

  pemToArrayBuffer(pem: string): ArrayBuffer {
    const b64Lines = pem.replace(/-----.*?-----/g, "").replace(/\s+/g, "");
    const b64Decoded = atob(b64Lines);
    const buffer = new Uint8Array(b64Decoded.length);
    for (let i = 0; i < b64Decoded.length; i++) {
      buffer[i] = b64Decoded.charCodeAt(i);
    }
    return buffer.buffer;
  }

  arrayBufferToBase64(buffer: ArrayBuffer): string {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)));
  }

  base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const len = binary.length;
    const buffer = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      buffer[i] = binary.charCodeAt(i);
    }
    return buffer.buffer;
  }

  async getKeyFromIndexedDB(keyName: string): Promise<CryptoKey | null> {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const tx = db.transaction("keys", "readonly");
      const store = tx.objectStore("keys");
      const keyRequest = store.get(keyName);
      keyRequest.onsuccess = () => resolve(keyRequest.result?.key || null);
      keyRequest.onerror = () => reject(keyRequest.error);
    });
  }

  async getPublicKeyFromIndexedDB(): Promise<ArrayBuffer | null> {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const tx = db.transaction("keys", "readonly");
      const store = tx.objectStore("keys");
      const keyRequest = store.get("publicKey");
      keyRequest.onsuccess = () => resolve(keyRequest.result?.key || null);
      keyRequest.onerror = () => reject(keyRequest.error);
    });
  }

  async saveKeyToIndexedDB(keyName: string, key: CryptoKey | ArrayBuffer) {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const tx = db.transaction("keys", "readwrite");
      tx.objectStore("keys").put({name: keyName, key});
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
    });
  }

  async doesPrivateKeyExist(): Promise<boolean> {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const tx = db.transaction("keys", "readonly");
      const store = tx.objectStore("keys");
      const keyRequest = store.get("privateKey");
      keyRequest.onsuccess = () => resolve(!!keyRequest.result);
      keyRequest.onerror = () => reject(keyRequest.error);
    });
  }

  public clearDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.databasePromise) {
        this.databasePromise.then(db => db.close());
        this.databasePromise = null;
      }

      const request = indexedDB.deleteDatabase("cryptoStore");

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error("Clearing IndexedDB blocked."));
    });
  }
}
